import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { UseQueryResult } from "@tanstack/react-query";
import React, { ReactElement, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useSellarUser } from "../../../../../../hooks";
import { useStorefrontLink } from "../../../../../../hooks/api/v2/storefrontLink/useStorefrontLink";
import { useSearchParamsPostcodeCheck } from "../../../../../../hooks/useSearchParamsPostcodeCheck";
import { useSellarCurrentBusiness } from "../../../../../../hooks/useSellarCurrentBusiness.shared";
import { StorefrontPostcodeDialog } from "./StorefrontPostcodeDialog";

export const StorefrontPostcodeDialogContainer = ({
  postcodeDialogActive,
  setPostcodeDialogActive,
  supplierQuery,
  unAuthorisedUserPostcode,
  setUnAuthorisedUserPostcode,
  title,
  description,
}: {
  postcodeDialogActive: boolean;
  setPostcodeDialogActive: (postcodeDialogActive: boolean) => void;
  unAuthorisedUserPostcode: string | undefined;
  supplierQuery?: UseQueryResult<SupplierGetResponseType | undefined>;
  setUnAuthorisedUserPostcode: (postcode: string) => void;
  title: string;
  description: string;
}): ReactElement => {
  const { loading } = useSellarUser();
  const { business, loading: businessLoading } = useSellarCurrentBusiness();
  const [searchParams] = useSearchParams();
  const storefrontLinkPassword =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const storefrontLinkQueryEnabled =
    !!storefrontLinkPassword && !!supplierQuery?.data;

  const storefrontLinkQuery = useStorefrontLink(
    storefrontLinkPassword,
    {
      SupplierId: supplierQuery?.data?.id ?? 0,
    },
    {
      enabled: storefrontLinkQueryEnabled,
    },
  );

  const storefrontLinkLoading =
    storefrontLinkQueryEnabled && storefrontLinkQuery.isPending;
  const postcodeParams = useSearchParamsPostcodeCheck();
  const { postcode, isValid: isPostcodeValid } = postcodeParams;
  const hasSupplierQuery = Boolean(supplierQuery);
  const supplierQueryPending = supplierQuery?.isPending ?? false;

  useEffect(() => {
    if (!unAuthorisedUserPostcode && postcode && isPostcodeValid) {
      setUnAuthorisedUserPostcode(postcode);
    }
  }, [
    unAuthorisedUserPostcode,
    postcode,
    isPostcodeValid,
    setUnAuthorisedUserPostcode,
  ]);

  useEffect(() => {
    if (
      !loading &&
      !unAuthorisedUserPostcode &&
      !business &&
      !businessLoading &&
      !storefrontLinkLoading &&
      !storefrontLinkQuery.data &&
      (!postcode || !isPostcodeValid) &&
      (!hasSupplierQuery || !supplierQueryPending)
    ) {
      setPostcodeDialogActive(true);
    }
  }, [
    unAuthorisedUserPostcode,
    loading,
    business,
    businessLoading,
    storefrontLinkQuery.data,
    storefrontLinkLoading,
    postcode,
    isPostcodeValid,
    hasSupplierQuery,
    supplierQueryPending,
    setPostcodeDialogActive,
  ]);

  return (
    <>
      <StorefrontPostcodeDialog
        active={postcodeDialogActive}
        onDismiss={() => setPostcodeDialogActive(false)}
        supplier={supplierQuery?.data}
        setUnAuthorisedUserPostcode={setUnAuthorisedUserPostcode}
        title={title}
        description={description}
      />
    </>
  );
};
